import { FiatPrice, RewardsPrice } from "redmond";
import {
  getTotalPriceText,
  getRewardText,
  twoDecimalFormatter,
  getPriceString,
  getPricesWithComma,
} from "halifax";

export const LOCATION_TEXT = "Tasmania, Australia";
export const WARNING_BANNER_COPY =
  "If you don't add this option, your booking is nonrefundable.";

export const WARNING_BANNER_GENERIC_COPY =
  "If you don't add this option, your booking will be subject to the hotel cancellation policy that you select.";

export const REFUNDABLE_ROOM_TITLE = `Make your hotel <strong>refundable</strong>.`;
export const REFUNDABLE_ROOM_TITLE_SEPARATE_LINES = `Make your hotel<br><strong>refundable</strong>.`;

export const REFUNDABLE_ROOM_SUBTITLE = (
  fiat: FiatPrice,
  reward: RewardsPrice | undefined,
  perNight: boolean,
  isCfarFeeRefundable: boolean
) =>
  `Plan your trip worry-free. Add the flexibility to cancel your hotel booking for any reason for a ${
    !isCfarFeeRefundable ? "nonrefundable" : ""
  } fee of <strong>${getTotalPriceText({
    price: fiat,
    priceFormatter: twoDecimalFormatter,
  })}</strong>${
    reward
      ? ` / ${getRewardText({
          reward,
        })}`
      : ""
  }${perNight ? " per night" : ""}.`;

const getRewardsUpdatedUx = ({ reward }: { reward: RewardsPrice }) => {
  const isFiat = reward.currency === "$";
  const isRewardCash = reward.currency.toLowerCase().includes("cash");

  const value = Math.ceil(reward.value);

  const formattedValue = isRewardCash
    ? value
    : `${getPricesWithComma(Math.ceil(value / 100) * 100)}`;

  return isFiat
    ? `${reward.currency}${value}`
    : `${isRewardCash ? "$" : ""}${formattedValue.toLocaleString()} ${
        reward.currencyDescription?.toLocaleLowerCase() ||
        reward.currency.toLocaleLowerCase()
      }`;
};

// Negative premium copy
export const STANDARDIZED_REFUNDABLE_ROOM_SUBTITLE =
  "Plan your trip worry-free. Add the flexibility to cancel your hotel booking for any reason.";

export const COVERAGE_UNDER_100_CFAR_DETAILS = ({
  expirationDateString,
  expirationEqualToCheckIn,
  coveragePercentage,
  coverageAmount,
}: {
  expirationDateString: string;
  expirationEqualToCheckIn: boolean;
  coveragePercentage: number;
  coverageAmount: string;
}) =>
  `Cancel for any reason up until <strong>${expirationDateString} ${
    expirationEqualToCheckIn ? " (check-in time)" : ""
  }</strong> and get a <strong>${coveragePercentage}% refund (${coverageAmount})</strong> back to your original payment method.`;

export const STANDARDIZED_FULLY_REFUNDABLE_CFAR_DETAILS = ({
  expirationDateString,
  expirationEqualToCheckIn,
  coverageAmount,
  priceIncludesTaxes,
}: {
  expirationDateString: string;
  expirationEqualToCheckIn: boolean;
  coverageAmount: string;
  priceIncludesTaxes: boolean;
}) => {
  const refundString = priceIncludesTaxes
    ? `full refund (${coverageAmount})`
    : `full refund (${coverageAmount} plus taxes)`;

  return `Cancel for any reason up until <strong>${expirationDateString} ${
    expirationEqualToCheckIn ? " (check-in time)" : ""
  }</strong> and get a <strong>${refundString}</strong> back to your original payment method.`;
};

export const STANDARDIZED_PARTIALLY_REFUNDABLE_CFAR_DETAILS = ({
  expirationDateString,
  expirationEqualToCheckIn,
  coverageAmount,
  priceIncludesTaxes,
}: {
  expirationDateString: string;
  expirationEqualToCheckIn: boolean;
  coverageAmount: string;
  priceIncludesTaxes: boolean;
}) => {
  const refundString = priceIncludesTaxes
    ? `refund of your hotel base rate plus taxes and fees (${coverageAmount})`
    : `refund of your hotel base rate and fees (${coverageAmount} plus taxes)`;

  return `Cancel for any reason up until <strong>${expirationDateString}${
    expirationEqualToCheckIn ? " (check-in time)" : ""
  }</strong> and get a <strong>${refundString}</strong> back to your original payment method.`;
};

export const REFUNDABLE_ROOM_SUBTITLE_UPDATED_UX = (
  fiat: FiatPrice,
  reward: RewardsPrice | undefined,
  perNight: boolean,
  isCfarFeeRefundable: boolean
) =>
  `Plan your trip worry-free. Add the flexibility to cancel your hotel booking for any reason for a ${
    !isCfarFeeRefundable ? "nonrefundable" : ""
  } fee of <strong>${getPriceString({
    price: Math.ceil(fiat.value),
    priceFormatter: twoDecimalFormatter,
  })}</strong>${
    reward
      ? ` / ${getRewardsUpdatedUx({
          reward,
        })}`
      : ""
  }${perNight ? " per night" : ""}.`;

export const CAP1_HOTEL_CFAR_REFUNDABLE_UPON_CANCELLATION = (
  cfarPremium: FiatPrice,
  perNight: boolean
) =>
  `This ${getPriceString({
    price: Math.ceil(cfarPremium.value),
    priceFormatter: twoDecimalFormatter,
  })} ${
    perNight ? "per night" : ""
  } fee is <strong>included in your refund upon cancellation.</strong>`;

export const CAP1_HOTEL_CFAR_NONREFUNDABLE_UPON_CANCELLATION = (
  cfarPremium: FiatPrice,
  perNight: boolean
) =>
  `This ${getPriceString({
    price: Math.ceil(cfarPremium.value),
    priceFormatter: twoDecimalFormatter,
  })} ${
    perNight ? "per night" : ""
  } fee is <strong>nonrefundable upon cancellation.</strong>`;

export const SELECT_AND_CONTINUE_BUTTON_COPY = "Select and continue";
export const READ_TERMS_AND_CONDITIONS_COPY = "Read terms and conditions";
export const VIEW_TERMS_AND_CONDITIONS_COPY = "View Terms and Conditions";

/*
  note: ESTIMATED_CFAR_CONTENT_HEIGHT is actually an upper bound to the height of CFAR content (with image-background header);
  in the future, it can be removed if we were to find a better solution. DIALOG_MARGIN is the same as the margin in MuiDialog-paperScrollPaper,
  which comes from material-ui.Dialog; MuiDialog-paperScrollPaper has max-height set to calc(100% - 2 * 32px)
*/
const ESTIMATED_CFAR_CONTENT_HEIGHT = 630;
const DIALOG_MARGIN = 32;
export const PARTIAL_SCROLL_THRESHOLD = `(max-height: ${
  ESTIMATED_CFAR_CONTENT_HEIGHT + 2 * DIALOG_MARGIN
}px)`;

// note: dayjs doesn't seem to have any format that looks like this, see https://day.js.org/docs/en/display/format
export const getNthDay = (day: number) => {
  const dayString = String(day);
  const last = +dayString.slice(-2);

  if (last > 3 && last < 21) return "th";
  switch (last % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};
